import { Fragment, useEffect, useState } from 'react';

import classNames from 'classnames';

import TimerIcon from '@assets/icons/timer.svg';

import styles from './countdown.module.scss';

const DEFAULT_END_DATE = '2022-11-25T00:00:00';

export function CountDown({
  endDate = DEFAULT_END_DATE,
  showLegend = true,
  showDays = false,
  variant = 'light',
  fontSize = 'text-xl',
  legendFontSize = 'text-md',
  barebones = false,
  onFinish = () => {}
}) {
  const [time, setTime] = useState({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00'
  });

  useEffect(() => {
    const prepareNumber = value =>
      Math.max(value, 0).toString().padStart(2, '0');

    const dateStr = (endDate || DEFAULT_END_DATE).replace(' ', 'T');
    const countDownDate = new Date(dateStr).getTime();

    const countDownClock = setInterval(() => {
      const distance = countDownDate - Date.now();

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));

      const hours = Math.floor(
        showDays
          ? (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          : distance / (1000 * 60 * 60)
      );

      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (distance < 0) {
        setTime({
          days: '00',
          hours: '00',
          minutes: '00',
          seconds: '00'
        });

        onFinish();
        clearInterval(countDownClock);

        return;
      }

      setTime({
        days: prepareNumber(days),
        hours: prepareNumber(hours),
        minutes: prepareNumber(minutes),
        seconds: prepareNumber(seconds)
      });
    }, 250);

    return () => clearInterval(countDownClock);
  }, [endDate, onFinish, showDays]);

  const timeLabels = key => {
    const labels = {
      days: 'DIAS',
      hours: 'HORAS',
      minutes: 'MIN',
      seconds: 'SEG'
    };

    return labels[key] || '';
  };

  const visibleTime = Object.entries(time).filter(
    ([key]) => showDays || key !== 'days'
  );

  return (
    <div
      className={classNames(
        {
          'bg-primary-darkest': variant === 'dark' && !barebones
        },
        'flex flex-col items-center justify-center'
      )}
    >
      <div className={classNames('flex flex-col', barebones && 'w-full')}>
        {variant === 'black-friday' && !barebones && (
          <p className="flex items-center self-center gap-2 text-center font-gotham font-bold lg:text-[18px]">
            A BLACK FRIDAY ESTÁ CHEGANDO
            <TimerIcon className={styles.timerIcon} />
          </p>
        )}

        <div
          className={classNames(
            'flex items-start justify-center',
            barebones ? 'w-full' : 'gap-1 md:justify-between',
            variant === 'black-friday-flash-sale' && !barebones && 'mt-6'
          )}
        >
          {visibleTime.map(([key, value], index) => {
            const isLastItem = index === visibleTime.length - 1;

            return (
              <Fragment key={key}>
                {barebones ? (
                  <div className="flex min-w-0 flex-1 flex-col items-center text-white">
                    <div
                      className={classNames(fontSize, 'font-bold leading-none')}
                    >
                      {value}
                    </div>

                    {showLegend && (
                      <div
                        className={classNames(legendFontSize, 'mt-2 uppercase')}
                      >
                        {timeLabels(key)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className={classNames('rounded-md border-0', {
                      'bg-gray-lightest': variant === 'light',
                      'bg-primary-medium': variant === 'dark',
                      'bg-transparent': variant === 'black-friday'
                    })}
                  >
                    <div
                      className={classNames(
                        'mr-1 flex align-center',
                        variant === 'black-friday-flash-sale' &&
                          'mx-1 justify-end'
                      )}
                    >
                      {value.split('').map((digit, digitIndex) => (
                        <div
                          key={`${key}-${digitIndex}`}
                          className={classNames(
                            'rounded-md border-0 text-center',
                            variant === 'black-friday-flash-sale' &&
                              `${styles.clockDigit} text-[3rem]`,
                            variant === 'black-friday' &&
                              `${fontSize} ${styles.clockDigit}`,
                            {
                              'm-1 mr-0 w-[30px] bg-white-lightest p-1 font-bold text-primary-dark':
                                variant === 'light',
                              'm-1 mr-0 w-[30px] bg-primary-dark p-1 font-bold text-white':
                                variant === 'dark',
                              'w-[28px] text-[3rem] leading-none lg:my-4 lg:w-[45px]':
                                variant === 'black-friday'
                            }
                          )}
                        >
                          {digit}
                        </div>
                      ))}
                    </div>

                    {showLegend && (
                      <div
                        className={classNames(
                          `text-center font-bold ${legendFontSize}`,
                          {
                            'text-primary-dark': variant === 'light',
                            'text-white': ['dark', 'black-friday'].includes(
                              variant
                            )
                          },
                          variant === 'black-friday-flash-sale' &&
                            'text-[14px] text-white'
                        )}
                      >
                        {timeLabels(key)}
                      </div>
                    )}
                  </div>
                )}

                {!isLastItem && (
                  <div
                    className={classNames(
                      barebones
                        ? `${fontSize} shrink-0 px-1 font-bold leading-none text-white`
                        : variant === 'black-friday'
                          ? styles.clockColon
                          : variant === 'black-friday-flash-sale'
                            ? styles.clockColonFlashSale
                            : 'text-[20px] font-bold text-white',
                      {
                        'mt-[3%] lg:mt-[7.5%] lg:text-[80px]':
                          variant === 'black-friday' && !barebones
                      }
                    )}
                  >
                    :
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
